import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { disconnectSocket } from '../lib/socket';
import { cn } from '../lib/utils';
import { CallbackBadge } from './CallbackBadge';
import { WhatsappBadge } from './WhatsappBadge';
import { SmsBadge } from './SmsBadge';

interface NavItem {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
  badge?: 'callback' | 'whatsapp' | 'sms';
  supervisorOnly?: boolean;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: 'D', label: 'Dashboard', end: true },
  { to: '/calls', icon: 'C', label: 'Calls' },
  { to: '/reports', icon: 'R', label: 'Reports', adminOnly: true },
  { to: '/callbacks', icon: 'B', label: 'Callbacks', badge: 'callback' },
  { to: '/whatsapp', icon: 'W', label: 'WhatsApp', badge: 'whatsapp' },
  { to: '/sms', icon: 'M', label: 'SMS', badge: 'sms' },
  { to: '/leads', icon: 'L', label: 'Leads' },
  { to: '/campaigns', icon: 'G', label: 'Campaigns' },
  { to: '/supervisor', icon: 'S', label: 'Supervisor', supervisorOnly: true },
  { to: '/compliance', icon: 'K', label: 'Compliance', supervisorOnly: true },
  { to: '/settings', icon: 'T', label: 'Settings' },
];

export function Sidebar(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);

  return (
    <aside className="hidden sm:flex h-full w-16 lg:w-60 flex-col border-r border-ink-200 bg-white shrink-0">
      <div className="border-b border-ink-200 p-3 lg:p-4 flex flex-col items-center lg:items-start">
        <div className="text-lg font-semibold tracking-tight lg:hidden" title="Marnic Dialler">M</div>
        <div className="hidden lg:block text-lg font-semibold tracking-tight">Marnic</div>
        <div className="hidden lg:block text-xs uppercase tracking-wider text-ink-500">Dialler</div>
      </div>
      <nav className="flex-1 space-y-1 p-2 lg:p-3 scroll-area">
        {visibleItems(user?.role).map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={inlineNavLinkClass} title={item.label}>
            <span className="grid h-5 w-5 place-items-center rounded bg-current/10 text-xs font-bold">{item.icon}</span>
            <span className="hidden lg:inline truncate">{item.label}</span>
            {item.badge === 'callback' && (
              <span className="hidden lg:inline-flex ml-auto">
                <CallbackBadge />
              </span>
            )}
            {item.badge === 'whatsapp' && (
              <span className="hidden lg:inline-flex ml-auto">
                <WhatsappBadge />
              </span>
            )}
            {item.badge === 'sms' && (
              <span className="hidden lg:inline-flex ml-auto">
                <SmsBadge />
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      {user && (
        <div className="border-t border-ink-200 p-2 lg:p-3">
          <div className="hidden lg:block">
            <div className="text-sm font-medium truncate">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-xs text-ink-500 capitalize">{user.role}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              disconnectSocket();
              clear();
            }}
            className="btn-ghost w-full lg:mt-2 justify-center lg:justify-start"
            title="Sign out"
          >
            <span className="font-bold">Q</span>
            <span className="hidden lg:inline">Sign out</span>
          </button>
        </div>
      )}
    </aside>
  );
}

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

export function MobileNavDrawer({ open, onClose }: DrawerProps): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sm:hidden fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} aria-hidden="true" />
      <aside className="relative flex h-full w-64 max-w-[80vw] flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-ink-200 p-4">
          <div>
            <div className="text-lg font-semibold tracking-tight">Marnic</div>
            <div className="text-xs uppercase tracking-wider text-ink-500">Dialler</div>
          </div>
          <button type="button" className="btn-ghost px-2" onClick={onClose} aria-label="Close navigation">
            X
          </button>
        </div>
        <nav className="flex-1 space-y-1 p-3 scroll-area">
          {visibleItems(user?.role).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={drawerNavLinkClass} onClick={onClose}>
              <span className="grid h-5 w-5 place-items-center rounded bg-current/10 text-xs font-bold">{item.icon}</span>
              <span className="truncate">{item.label}</span>
              {item.badge === 'callback' && <CallbackBadge />}
              {item.badge === 'whatsapp' && <WhatsappBadge />}
              {item.badge === 'sms' && <SmsBadge />}
            </NavLink>
          ))}
        </nav>
        {user && (
          <div className="border-t border-ink-200 p-3">
            <div className="text-sm font-medium truncate">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-xs text-ink-500 capitalize">{user.role}</div>
            <button
              type="button"
              onClick={() => {
                disconnectSocket();
                clear();
                onClose();
              }}
              className="btn-ghost mt-2 w-full justify-start"
            >
              Q Sign out
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

function visibleItems(role: string | undefined): NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if (item.adminOnly && role !== 'admin') return false;
    if (item.supervisorOnly && role === 'agent') return false;
    return true;
  });
}

function inlineNavLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-3 rounded-md px-2 lg:px-3 py-2 text-sm font-medium transition',
    'justify-center lg:justify-start',
    isActive ? 'bg-brand-600 text-white shadow-sm' : 'text-ink-600 hover:bg-ink-100',
  );
}

function drawerNavLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition justify-start',
    isActive ? 'bg-brand-600 text-white shadow-sm' : 'text-ink-600 hover:bg-ink-100',
  );
}
